import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Clock, MapPin, ChevronRight, X, Check } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { formatPrice } from '../lib/utils';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TourCard = ({ tour, onBook, language, onOpenDetail }) => {
  const { t } = useLanguage();
  const [showMap, setShowMap] = useState(false);

  const name = tour.name[language] || tour.name.es;
  const description = tour.description[language] || tour.description.es;

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="group relative bg-white rounded-2xl overflow-hidden shadow-lg card-lift h-full flex flex-col"
        data-testid={`tour-card-${tour.id}`}
      >
        {/* Image - Clickable to open detail */}
        <div 
          className="relative aspect-[4/3] overflow-hidden cursor-pointer"
          onClick={() => onOpenDetail(tour)}
          data-testid={`tour-image-${tour.id}`}
        >
          <img
            src={tour.image}
            alt={name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          />
          {/* Price Tag */}
          <div className="absolute top-4 right-4 bg-[#C2410C] text-white px-4 py-2 rounded-full font-bold shadow-lg">
            {tour.id === 'un-dia-madrid' ? (
              <span>{t.tours.from} {formatPrice(tour.price)}</span>
            ) : (
              <span>{formatPrice(tour.price)}</span>
            )}
          </div>
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          {/* Click hint */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
            <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity font-medium bg-black/50 px-4 py-2 rounded-full">
              Ver detalles
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 flex flex-col flex-1">
          <h3 className="font-display text-xl font-bold text-[#1C1917] mb-2">
            {name}
          </h3>
          
          {/* Duration */}
          <div className="flex items-center gap-2 text-[#57534E] mb-3">
            <Clock className="h-4 w-4" />
            <span className="text-sm">{t.tours.duration}: {tour.duration}</span>
          </div>

          <p className="text-[#57534E] text-sm mb-4 line-clamp-2 flex-1">
            {description}
          </p>

          {/* Actions */}
          <div className="flex items-center gap-3 mt-auto">
            <Button
              onClick={() => setShowMap(true)}
              variant="outline"
              size="sm"
              className="flex-1 border-[#C2410C] text-[#C2410C] hover:bg-[#C2410C]/10 rounded-full"
              data-testid={`tour-map-btn-${tour.id}`}
            >
              <MapPin className="h-4 w-4 mr-2" />
              {t.tours.viewMap}
            </Button>
            <Button
              onClick={() => onBook(tour)}
              size="sm"
              className="flex-1 bg-[#C2410C] hover:bg-[#9A3412] text-white rounded-full"
              data-testid={`tour-book-btn-${tour.id}`}
            >
              {t.tours.book}
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Map Modal */}
      <Dialog open={showMap} onOpenChange={setShowMap}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{name}</DialogTitle>
          </DialogHeader>
          {tour.map_route && tour.map_route.length >= 2 ? (
            <>
              <div className="aspect-video bg-[#E7E5E4] rounded-lg overflow-hidden relative">
                {(() => {
                  const points = tour.map_route;
                  const origin = `${points[0].lat},${points[0].lng}`;
                  const destination = `${points[points.length - 1].lat},${points[points.length - 1].lng}`;
                  const intermediate = points.slice(1, -1);
                  // Google Maps Embed API supports ~20 waypoints max, sample if needed
                  let waypoints = intermediate;
                  if (intermediate.length > 20) {
                    const step = intermediate.length / 20;
                    waypoints = Array.from({ length: 20 }, (_, i) => intermediate[Math.min(Math.floor(i * step), intermediate.length - 1)]);
                  }
                  const waypointStr = waypoints.map(p => `${p.lat},${p.lng}`).join('|');
                  return (
                    <iframe
                      title={`Mapa de ${name}`}
                      width="100%"
                      height="100%"
                      frameBorder="0"
                      style={{ border: 0 }}
                      referrerPolicy="no-referrer-when-downgrade"
                      src={`https://www.google.com/maps/embed/v1/directions?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&origin=${origin}&destination=${destination}&waypoints=${waypointStr}&mode=driving`}
                      allowFullScreen
                    />
                  );
                })()}
              </div>
              <div className="mt-4">
                <h4 className="font-semibold mb-2 text-[#1C1917]">Puntos del recorrido:</h4>
                <ul className="grid grid-cols-2 gap-2">
                  {tour.map_route.map((point, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-[#57534E]">
                      <span className="w-6 h-6 rounded-full bg-[#C2410C] text-white flex items-center justify-center text-xs">
                        {idx + 1}
                      </span>
                      {point.name || `Punto ${idx + 1}`}
                    </li>
                  ))}
                </ul>
              </div>
            </>
          ) : (
            <div className="py-12 text-center text-[#78716C]">
              <MapPin className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>Mapa no disponible para este tour</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

// Tour Detail Modal Component
const TourDetailModal = ({ tour, isOpen, onClose, onBook, language }) => {
  const { t } = useLanguage();
  const [pois, setPois] = useState([]);
  
  useEffect(() => {
    if (tour?.id && isOpen) {
      axios.get(`${API}/tours/${tour.id}/pois`).then(res => setPois(res.data)).catch(() => {});
    } else {
      setPois([]);
    }
  }, [tour?.id, isOpen]);

  if (!tour) return null;

  const name = tour.name[language] || tour.name.es;
  const fullDescription = tour.full_description?.[language] || tour.full_description?.es || tour.description[language] || tour.description.es;

  // Resolve gallery items - can be strings (legacy) or objects with media_id
  const galleryImages = (tour.gallery || []).map(item => {
    if (typeof item === 'string') return item;
    if (item.url) return item.url;
    if (item.media_id) return `${API}/media/file/${item.media_id}`;
    return null;
  }).filter(Boolean);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl">{name}</DialogTitle>
        </DialogHeader>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Image Gallery */}
          <div className="space-y-4">
            <div className="aspect-[4/3] rounded-xl overflow-hidden">
              <img src={tour.image} alt={name} className="w-full h-full object-cover" />
            </div>
            {galleryImages.length > 0 && (
              <div className="grid grid-cols-3 gap-2">
                {galleryImages.map((img, idx) => (
                  <div key={idx} className="aspect-square rounded-lg overflow-hidden">
                    <img src={img} alt={`${name} ${idx + 1}`} className="w-full h-full object-cover hover:scale-110 transition-transform duration-300" />
                  </div>
                ))}
              </div>
            )}

            <Button
              onClick={() => { onClose(); onBook(tour); }}
              className="w-full bg-[#C2410C] hover:bg-[#9A3412] text-white rounded-full py-6 text-lg"
              data-testid={`tour-detail-book-btn-${tour.id}`}
            >
              {t.tours.book}
              <ChevronRight className="h-5 w-5 ml-2" />
            </Button>
          </div>

          {/* Details */}
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-[#57534E]">
                <Clock className="h-5 w-5" />
                <span>{t.tours.duration}: {tour.duration}</span>
              </div>
              <div className="text-right">
                {tour.id === 'un-dia-madrid' && tour.price_6h ? (
                  <div>
                    <p className="text-sm text-[#57534E]">3h: <span className="font-bold text-[#C2410C]">{formatPrice(tour.price)}</span></p>
                    <p className="text-sm text-[#57534E]">6h: <span className="font-bold text-[#C2410C]">{formatPrice(tour.price_6h)}</span></p>
                  </div>
                ) : (
                  <p className="text-2xl font-bold text-[#C2410C]">{formatPrice(tour.price)}</p>
                )}
              </div>
            </div>

            <div><p className="text-[#57534E] leading-relaxed">{fullDescription}</p></div>

            {tour.includes && tour.includes.length > 0 && (
              <div>
                <h4 className="font-semibold text-[#1C1917] mb-3">Incluye:</h4>
                <ul className="grid grid-cols-1 gap-2">
                  {tour.includes.map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-[#57534E]">
                      <Check className="h-4 w-4 text-[#C2410C] flex-shrink-0" />
                      {item[language] || item.es || item}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Points of Interest */}
            {pois.length > 0 && (
              <div>
                <h4 className="font-semibold text-[#1C1917] mb-3">
                  <MapPin className="h-4 w-4 inline mr-1.5 text-[#C2410C]" />
                  Puntos de interes
                </h4>
                <div className="space-y-3">
                  {pois.map((poi, idx) => (
                    <div key={poi.id} className="flex gap-3 items-start" data-testid={`tour-poi-${poi.id}`}>
                      <span className="w-6 h-6 rounded-full bg-[#C2410C] text-white flex items-center justify-center text-xs flex-shrink-0 mt-0.5">{idx + 1}</span>
                      <div>
                        <p className="font-medium text-sm text-[#1C1917]">{poi.name?.[language] || poi.name?.es}</p>
                        <p className="text-xs text-[#78716C]">{poi.short_description?.[language] || poi.short_description?.es}</p>
                        {poi.address && <p className="text-xs text-[#A8A29E] mt-0.5">{poi.address}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Tours = ({ onBookTour }) => {
  const { language, t } = useLanguage();
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTour, setSelectedTour] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  useEffect(() => {
    const fetchTours = async () => {
      try {
        const response = await axios.get(`${API}/tours`);
        setTours(response.data);
      } catch (error) {
        console.error('Error fetching tours:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTours();
  }, []);

  const handleOpenDetail = (tour) => {
    setSelectedTour(tour);
    setShowDetail(true);
  };

  const handleCloseDetail = () => {
    setShowDetail(false);
    setSelectedTour(null);
  };

  return (
    <section 
      id="tours" 
      className="py-24 md:py-32 bg-[#FDFBF7]"
      data-testid="tours-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-[#C2410C] mb-4 font-semibold">
            Tours
          </p>
          <h2 className="font-display text-4xl sm:text-5xl font-bold text-[#1C1917] mb-4">
            {t.tours.title}
          </h2>
          <p className="text-lg text-[#57534E] max-w-2xl mx-auto">
            {t.tours.subtitle}
          </p>
        </motion.div>

        {/* Tours Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white rounded-2xl h-96 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {tours.map((tour, index) => (
              <motion.div
                key={tour.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <TourCard
                  tour={tour}
                  language={language}
                  onBook={onBookTour}
                  onOpenDetail={handleOpenDetail}
                />
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Tour Detail Modal */}
      <TourDetailModal
        tour={selectedTour}
        isOpen={showDetail}
        onClose={handleCloseDetail}
        onBook={onBookTour}
        language={language}
      />
    </section>
  );
};

export default Tours;
